export async function callDirectGeminiAPI(promptText, apiKey, systemInstruction = null) {
  if (!apiKey || !apiKey.trim()) {
    throw new Error('Direct Gemini API key is missing.');
  }

  const candidateModels = [
    'gemini-2.5-flash',
    'gemini-3.5-flash',
    'gemini-3-flash-preview',
    'gemini-3.5-flash-lite',
    'gemini-3.1-flash-lite'
  ];

  const requestBody = {
    contents: [{
      parts: [{ text: promptText }]
    }]
  };

  if (systemInstruction && typeof systemInstruction === 'string' && systemInstruction.trim() !== '') {
    requestBody.systemInstruction = {
      parts: [{ text: systemInstruction.trim() }]
    };
  }

  let lastError = null;

  for (const model of candidateModels) {
    try {
      const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey.trim()}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        let errorMsg = `HTTP ${response.status}`;
        try {
          const errData = await response.json();
          if (errData?.error?.message) {
            errorMsg = errData.error.message;
          }
        } catch (e) {}
        lastError = new Error(`Model ${model} failed: ${errorMsg}`);
        continue;
      }

      const data = await response.json();
      const text = data?.candidates?.[0]?.content?.parts?.[0]?.text;
      if (text) {
        return text;
      }
    } catch (err) {
      lastError = err;
    }
  }

  throw (lastError || new Error('Direct Gemini API call failed on all candidate models.'));
}

export async function callGeminiCoach(promptText, options = {}) {
  const fallbackKey = (options.customKey && options.customKey.trim()) ||
    (typeof window !== 'undefined' && window.state && window.state.geminiApiKey) ||
    (typeof window !== 'undefined' && window.store && window.store.geminiApiKey) ||
    null;

  const payload = {
    prompt: promptText,
    customKey: fallbackKey,
    systemInstruction: options.systemInstruction || null
  };

  const headers = {
    'Content-Type': 'application/json'
  };

  const sb = options.supabaseClient || (typeof window !== 'undefined' && window.supabaseClient) || null;
  if (sb && sb.auth && typeof sb.auth.getSession === 'function') {
    try {
      const { data: { session } } = await sb.auth.getSession();
      if (session && session.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
    } catch (e) {}
  }

  let proxyError = null;
  let proxyStatus = 0;

  try {
    let response = await fetch('/api/coach', {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(payload)
    });

    if ((response.status === 401 || response.status === 403) && sb && sb.auth && typeof sb.auth.refreshSession === 'function') {
      try {
        const refreshed = await sb.auth.refreshSession();
        if (refreshed && refreshed.access_token) {
          headers['Authorization'] = `Bearer ${refreshed.access_token}`;
          response = await fetch('/api/coach', {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(payload)
          });
        }
      } catch (_) {}
    }

    proxyStatus = response.status;

    if (response.ok) {
      const data = await response.json();
      if (data && data.text) {
        return data.text;
      }
      throw new Error('RoxKit AI Coach returned an empty response.');
    }

    let errMsg = `RoxKit AI Coach error (Status ${response.status})`;
    try {
      const errJson = await response.json();
      if (errJson && (errJson.error || errJson.detail)) {
        errMsg = errJson.error || errJson.detail;
      }
    } catch (e) {}
    proxyError = new Error(errMsg);
  } catch (err) {
    proxyError = err;
  }

  // If proxy failed and athlete provided an API key (or has one saved), fall back to direct Google Gemini API
  if (fallbackKey) {
    console.warn(`[AI Coach] Proxy failed (${proxyError?.message || 'unknown'}). Falling back to direct Gemini API with athlete key.`);
    try {
      return await callDirectGeminiAPI(promptText, fallbackKey, options.systemInstruction);
    } catch (directErr) {
      console.error('[AI Coach] Direct Gemini API fallback also failed:', directErr);
      const directMsg = String(directErr?.message || '');
      if (/prepayment credits are depleted|billing|quota/i.test(directMsg)) {
        // Purge depleted personal AI Studio key so future calls rely on RoxKit Cloud Vertex AI
        if (typeof window !== 'undefined') {
          if (window.state && window.state.geminiApiKey === fallbackKey) window.state.geminiApiKey = '';
          if (window.store && window.store.geminiApiKey === fallbackKey) window.store.geminiApiKey = '';
        }
        if (proxyStatus === 401 || proxyStatus === 403) {
          throw new Error('AI Coach error: Session expired. Please sign in again to use RoxKit Cloud AI.');
        }
        if (proxyError) {
          throw new Error(`AI Coach error: ${proxyError.message}`);
        }
      }
      throw new Error(`AI Coach error: ${directErr.message || proxyError?.message}`);
    }
  }

  // No key available and proxy failed
  throw proxyError || new Error('RoxKit AI Coach service is temporarily unavailable.');
}

import { generateTelemetryPrompt, generateSummaryPrompt, generateDictationPrompt } from './prompts.js';

export async function runAIEvaluation(workout, options = {}) {
  if (!workout) return null;
  const isDistanceStation = options.isDistanceStation || null;
  const telemetry = generateTelemetryPrompt(workout, isDistanceStation);
  const promptText = generateSummaryPrompt(telemetry, options.customInstructions);
  return await callGeminiCoach(promptText, options);
}

export async function processVoiceCommandWithGemini(transcript, options = {}) {
  if (!transcript || !transcript.trim()) {
    throw new Error('Transcript is required for voice command processing.');
  }
  const promptText = generateDictationPrompt(transcript);
  const responseText = await callGeminiCoach(promptText, options);

  let jsonText = (responseText || '').trim();
  if (jsonText.startsWith('```json')) {
    jsonText = jsonText.substring(7);
  } else if (jsonText.startsWith('```')) {
    jsonText = jsonText.substring(3);
  }
  if (jsonText.endsWith('```')) {
    jsonText = jsonText.substring(0, jsonText.length - 3);
  }
  jsonText = jsonText.trim();

  return JSON.parse(jsonText);
}

